
import { Button } from '@/components/ui/button';
import { ArrowLeft, X } from 'lucide-react';

export interface PageHeaderProps {
  title: string;
  variant?: 'title-only' | 'title-with-exit' | 'back-with-title' | 'back-title-action' | 'progress';
  onBack?: () => void;
  onExit?: () => void;
  onAction?: () => void;
  actionLabel?: string;
  actionVariant?: 'default' | 'ghost' | 'outline';
  progress?: number;
  progressSubtext?: string;
  className?: string;
}

const PageHeader = ({
  title,
  variant = 'title-only',
  onBack,
  onExit,
  onAction,
  actionLabel,
  actionVariant = 'ghost',
  progress,
  progressSubtext,
  className = ''
}: PageHeaderProps) => {
  return (
    <div className={`bg-black text-white p-4 flex-shrink-0 ${className}`}>
      <div className="grid grid-cols-3 items-center">
        {/* Left Section - Fixed width for consistency */}
        <div className="flex justify-start min-w-0">
          {(variant === 'back-with-title' || variant === 'back-title-action') && onBack && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onBack}
              className="text-white hover:bg-white/10 flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Center Section - Title always centered */}
        <div className="flex justify-center">
          <h1 className="text-lg font-semibold text-center truncate">{title}</h1>
        </div>

        {/* Right Section - Fixed width for consistency */}
        <div className="flex justify-end min-w-0">
          {(variant === 'title-with-exit' || variant === 'progress') && onExit && (
            <Button
              onClick={onExit}
              variant="ghost"
              className="text-white hover:bg-gray-800 h-8 px-3 text-sm flex-shrink-0"
            >
              Exit
            </Button>
          )}
          {variant === 'back-title-action' && onAction && actionLabel && (
            <Button
              onClick={onAction}
              variant={actionVariant}
              className="text-white hover:bg-gray-800 h-8 px-3 text-sm flex-shrink-0"
            >
              <span className="truncate max-w-20">{actionLabel}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar Section */}
      {variant === 'progress' && progress !== undefined && (
        <div className="mt-3">
          <div className="w-full bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          {progressSubtext && (
            <p className="text-sm opacity-90 mt-2">{progressSubtext}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
